import type { ABTestRouteConfig } from '@app-studio-web/ui/types/abtest'
import alzheimerOnboardingRoutes from '@pages/alzheimer/onboarding/routes'
import baselineOnboardingRoutes from '@pages/baseline/onboarding/routes'
import v1OnboardingRoutes from '@pages/v1/onboarding/routes'

/**
 * A/B Test Configuration for Longevity Compass
 *
 * Current variants:
 * - baseline: 35-page onboarding flow
 * - v1: baseline + memory sequence game & feedback page (after exercise block)
 */

interface RouteDefinition {
  path?: string
  handle?: { skipInNavigation?: boolean }
  children?: RouteDefinition[]
}

function extractChildPaths(onboardingRoutesDef: RouteDefinition[]): string[] {
  const parent = onboardingRoutesDef[0]
  if (!parent || !parent.children) return []
  return parent.children
    .filter((child) => !child.handle?.skipInNavigation)
    .map((child) => child.path as string)
}

function extractFlatPaths(routes: { path: string }[]): string[] {
  return routes.map((route) => route.path)
}

const baselineOrder = extractChildPaths(
  baselineOnboardingRoutes as RouteDefinition[],
)
const v1Order = extractChildPaths(v1OnboardingRoutes as RouteDefinition[])

export const longevityCompassABTestRouteConfig: ABTestRouteConfig = {
  baseline: baselineOrder,
  variants: {
    v1: v1Order,
  },
}

export const alzheimerRouteConfig: ABTestRouteConfig = {
  baseline: extractFlatPaths(alzheimerOnboardingRoutes),
  variants: {},
}

/**
 * Get A/B test configuration from environment variables
 */
export function getABTestConfig() {
  const clientSecret = import.meta.env.VITE_CONFIDENCE_CLIENT_SECRET as string
  const flagName =
    (import.meta.env.VITE_CONFIDENCE_FLAG_NAME as string) || 'lc-memorygame'

  // Only enable A/B testing if client secret is provided
  if (!clientSecret) {
    console.warn('No Confidence client secret provided, A/B testing disabled')
    return null
  }

  return {
    clientSecret,
    flagName,
    defaultVariant: 'baseline',
    enabledVariants: ['baseline', 'v1'],
  }
}
